/**
 * Google Analytics 4 — wrapper minimo, non-blocking (ANL-01, ANL-02, ANL-03).
 *
 * Por que esse arquivo existe:
 *  - ANL-01: GA4 instalado via gtag.js com Measurement ID em env var
 *  - ANL-02: evento `whatsapp_click` disparado em cada CTA com parametro
 *    `source` (hero-cta, navbar-cta, floating-cta, final-cta, footer-cta)
 *  - ANL-03: gtag.js carregado async via injecao dinamica de <script> —
 *    nao bloqueia o first paint (Lighthouse Performance > 90)
 *
 * Comportamento em dev / sem Measurement ID:
 *  - `initAnalytics()` no-op se `VITE_GA4_MEASUREMENT_ID` esta vazio
 *  - `trackWhatsAppClick()` no-op se `window.gtag` nao existe
 *  - Zero erro no console — desenvolvedor pode trabalhar sem GA4 conectado
 *
 * Privacidade: SOMENTE o evento `whatsapp_click` com `source` e enviado.
 * Nenhum PII (telefone, mensagem, IP). GA4 ja faz IP anonymization por
 * default em territorio europeu / Brasil.
 */

const MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let initialized = false;

/**
 * Inicializa GA4 — injeta o script `gtag.js` de forma assincrona e configura
 * o `Measurement ID` lido de `import.meta.env.VITE_GA4_MEASUREMENT_ID`.
 *
 * Idempotente: chamadas repetidas viram no-op apos a primeira.
 * Silently no-op quando o Measurement ID nao esta setado (dev, preview sem
 * env var, etc).
 *
 * Chamado uma vez em `src/main.tsx` no bootstrap.
 */
export function initAnalytics(): void {
  if (initialized) return;
  if (MEASUREMENT_ID === undefined || MEASUREMENT_ID === '') return;
  initialized = true;

  // Inject gtag.js asynchronously (ANL-03 — non-blocking)
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  const dataLayer: unknown[] = window.dataLayer ?? [];
  window.dataLayer = dataLayer;

  // gtag e uma funcao que push args para o dataLayer; o gtag.js processa
  // assincronamente quando o script carrega.
  const gtag = (...args: unknown[]): void => {
    dataLayer.push(args);
  };
  window.gtag = gtag;

  gtag('js', new Date());
  gtag('config', MEASUREMENT_ID);
}

/**
 * Dispara o evento `whatsapp_click` no GA4 com parametro `source`.
 *
 * `source` identifica a origem do clique para que a equipe possa medir qual
 * dobra/CTA da landing converte mais (PRD §15 KPIs):
 *  - 'hero-cta': CTA primario da primeira dobra (Hero)
 *  - 'navbar-cta': botao verde sempre visivel no navbar (desktop + mobile)
 *  - 'floating-cta': FAB no canto inferior direito
 *  - 'final-cta': CTA na faixa verde escura no final da landing
 *  - 'footer-cta': link de WhatsApp na coluna de contato do rodape
 *
 * No-op quando `window.gtag` nao foi inicializado (dev, ou GA4 falhou em
 * carregar). NUNCA bloqueia ou retarda a navegacao para `wa.me` — o tracking
 * dispara sincronamente antes do browser seguir o href, mas se falhar nao
 * impede a conversao.
 */
export function trackWhatsAppClick(source: string): void {
  if (typeof window === 'undefined') return;
  if (typeof window.gtag !== 'function') return;

  window.gtag('event', 'whatsapp_click', { source });
}
